import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, CheckCircle2, LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CameraCapture } from "@/components/CameraCapture";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

/* Admin face enrolment: live camera capture only (no file input anywhere).
   Blocked at the API layer unless biometric consent is on file (spec §9). */
export default function EnrolPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const [blob, setBlob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  const { data: user, isLoading } = useQuery({
    queryKey: ["user", userId],
    queryFn: async () => (await api.get(`/users/${userId}`)).data,
  });

  // Face engine diagnostic — warns BEFORE capture if the sidecar is down,
  // instead of a bare "Face service unavailable" only after pressing save.
  const { data: faceHealth } = useQuery({
    queryKey: ["face-health"],
    queryFn: async () => (await api.get("/admin/face-health")).data,
    refetchInterval: 15000,
  });

  const submit = async () => {
    setBusy(true);
    setError("");
    const fd = new FormData();
    fd.append("photo", blob, "reference.jpg");
    try {
      const { data } = await api.post(`/users/${userId}/enrol`, fd);
      setDone(data);
      toast.success(t("enrolSuccess"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "no_face_detected") setError(t("noFace"));
      else if (detail === "pdpa_acceptance_required") setError(t("pdpaRequiredEnrol"));
      else if (err?.response?.status === 503) setError(t("faceEngineDown"));
      else setError(detail || "Error");
      setBlob(null);
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoaderCircle className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (done) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6">
        <CheckCircle2 className="h-16 w-16 text-emerald-600" />
        <h1 data-testid="enrol-success-title" className="text-2xl font-semibold">{t("enrolSuccess")}</h1>
        <p className="text-sm text-muted-foreground">
          {user?.fullName} · score {done.detectionScore?.toFixed(3)}
        </p>
        <Button onClick={() => navigate("/admin/users")} data-testid="enrol-done-button" className="h-11 px-8">
          {t("done")}
        </Button>
      </div>
    );
  }

  // HR consent-on-file gate disabled per user request — only PDPA acceptance
  // (in-app consent with selfie) is required before enrolment
  if (user && !user.pdpaAcceptedAt) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
        <AlertTriangle className="h-12 w-12 text-amber-600" />
        <p data-testid="consent-required-message" className="max-w-sm text-sm">
          {t("pdpaRequiredEnrol")} — {user.fullName}
        </p>
        <Button variant="secondary" onClick={() => navigate("/admin/users")} data-testid="enrol-back-button">
          <ArrowLeft className="mr-2 h-4 w-4" /> {t("back")}
        </Button>
      </div>
    );
  }

  return (
    <div className="rk-camera-screen flex flex-col">
      <div className="rk-safe-top z-10 flex items-center justify-between px-4 py-2">
        <Button variant="ghost" size="icon" onClick={() => navigate("/admin/users")}
          data-testid="enrol-back-button" className="text-white hover:bg-white/10">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <span className="text-sm font-semibold">
          {t("enrolFace")} — {user?.fullName}
        </span>
        <span className="w-9" />
      </div>
      <p className="z-10 mx-4 mb-1 rounded-lg bg-black/50 p-2 text-center text-xs text-white/80">
        {t("enrolInstructions")}
      </p>

      {faceHealth && faceHealth.faceService !== "ok" && (
        <p data-testid="face-engine-warning"
          className="z-10 mx-4 mb-1 flex items-start gap-2 rounded-lg bg-rose-950/80 p-3 text-xs leading-5 text-rose-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          {faceHealth.faceService === "loading" ? t("faceEngineLoading") : t("faceEngineDown")}
        </p>
      )}

      <CameraCapture
        onCapture={setBlob}
        capturedBlob={blob}
        onRetake={() => { setBlob(null); setError(""); }}
        disabled={busy}
      />

      {(blob || error) && (
        <div className="z-10 space-y-3 bg-black/70 p-4 rk-safe-bottom">
          {error && <p data-testid="enrol-error" className="text-center text-sm font-medium text-rose-300">{error}</p>}
          {blob && (
            <Button data-testid="enrol-submit-button" onClick={submit} disabled={busy}
              className="h-14 w-full text-base font-semibold">
              {busy ? (<><LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> {t("submitting")}</>) : t("save")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
